// Viewport queries for components whose markup (not only CSS) differs between phone and PC.
import { useEffect, useState, useSyncExternalStore } from 'react';

export const DESK = '(min-width: 900px)';

export function useMedia(query: string): boolean {
  return useSyncExternalStore(
    (cb) => {
      const m = typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(query) : null;
      m?.addEventListener('change', cb);
      return () => m?.removeEventListener('change', cb);
    },
    () => (typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(query).matches : false),
    () => false,
  );
}

/** True at ≥ 900px (the PC layout). */
export function useDesk(): boolean {
  return useMedia(DESK);
}

/**
 * Width of an element (ResizeObserver), for components whose layout depends on the space they
 * get rather than on the viewport (an inflection table in a side panel or a form column).
 */
export function useWidth<T extends HTMLElement>(): [(el: T | null) => void, number | null] {
  const [width, setWidth] = useState<number | null>(null);
  const [el, setEl] = useState<T | null>(null);
  useEffect(() => {
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (w !== undefined) setWidth(Math.round(w));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [el]);
  return [setEl, width];
}
